import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import styles from "./CampaignDetailScreen.module.scss";
import classNames from "classnames";
import updateCampaignsService from "../../../apis/services/admin/updateCampaignsService";
import { toast } from "react-toastify";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";

// Campaign status configuration
const CAMPAIGN_STATUS_CONFIG = {
  PENDING: { label: "Na čekanju", color: "#9CA3AF", bgColor: "#F3F4F6" },
  IN_PROGRESS: { label: "U toku", color: "#3B82F6", bgColor: "#DBEAFE" },
  COMPLETED: { label: "Završeno", color: "#22C55E", bgColor: "#DCFCE7" },
  CANCELLED: { label: "Otkazano", color: "#EF4444", bgColor: "#FEE2E2" },
};

// Device status configuration
const DEVICE_STATUS_CONFIG = {
  PENDING: { label: "Na čekanju", color: "#9CA3AF", bgColor: "#F3F4F6" },
  COMMAND_SENT: { label: "Komanda poslata", color: "#3B82F6", bgColor: "#DBEAFE" },
  UPDATED: { label: "Ažurirano", color: "#22C55E", bgColor: "#DCFCE7" },
  FAILED: { label: "Neuspešno", color: "#EF4444", bgColor: "#FEE2E2" },
  SKIPPED: { label: "Preskočeno", color: "#F59E0B", bgColor: "#FEF3C7" },
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return moment(dateString).format("DD.MM.YYYY HH:mm");
};

const CampaignDetailScreen = () => {
  const { id } = useParams();
  const history = useHistory();
  
  const [campaign, setCampaign] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deviceStatusFilter, setDeviceStatusFilter] = useState("");
  
  // Cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Fetch campaign details
  const fetchCampaign = useCallback(async () => {
    try {
      const response = await updateCampaignsService.fetchCampaign(id);
      setCampaign(response);
    } catch (err) {
      console.error("Error fetching campaign:", err);
      setError("Greška pri učitavanju kampanje");
    }
  }, [id]);

  // Fetch campaign devices
  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const params = { perPage: 100 };
      if (deviceStatusFilter) params.status = deviceStatusFilter;
      
      const response = await updateCampaignsService.fetchCampaignDevices(id, params);
      setDevices(response?.data || response || []);
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setDevicesLoading(false);
    }
  }, [id, deviceStatusFilter]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCampaign();
      await fetchDevices();
      setLoading(false);
    };
    loadData();
  }, [fetchCampaign, fetchDevices]);

  // Refresh devices when filter changes
  useEffect(() => {
    if (!loading) {
      fetchDevices();
    }
  }, [deviceStatusFilter]);

  // Start campaign
  const handleStart = async () => {
    try {
      await updateCampaignsService.startCampaign(id);
      toast.success("Kampanja pokrenuta! Komande se šalju uređajima...");
      fetchCampaign();
      fetchDevices();
    } catch (err) {
      console.error("Error starting campaign:", err);
      toast.error("Greška pri pokretanju kampanje");
    }
  };

  // Cancel campaign
  const handleCancel = async () => {
    try {
      await updateCampaignsService.cancelCampaign(id);
      toast.success("Kampanja otkazana");
      setCancelModalOpen(false);
      fetchCampaign();
    } catch (err) {
      console.error("Error cancelling campaign:", err);
      toast.error("Greška pri otkazivanju kampanje");
    }
  };

  // Refresh campaign (check for updates)
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await updateCampaignsService.refreshCampaign(id);
      await fetchCampaign();
      await fetchDevices();
      toast.success("Podaci osveženi");
    } catch (err) {
      console.error("Error refreshing campaign:", err);
      toast.error("Greška pri osvežavanju");
    } finally {
      setRefreshing(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className={styles.content}>
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Učitavanje...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <DashboardLayout>
        <div className={styles.content}>
          <div className={styles.errorState}>
            <p>⚠️ {error}</p>
            <button className={styles.retryButton} onClick={() => window.location.reload()}>
              Pokušaj ponovo
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) return null;

  const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.PENDING;
  const progress = campaign.totalDevices > 0 
    ? (campaign.updatedDevices / campaign.totalDevices) * 100 
    : 0;

  return (
    <DashboardLayout>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button 
              className={styles.backButton}
              onClick={() => history.push("/update-campaigns")}
            >
              ← Nazad
            </button>
            <h1 className={styles.title}>{campaign.name}</h1>
            <span 
              className={styles.statusBadge}
              style={{ 
                backgroundColor: statusConfig.bgColor, 
                color: statusConfig.color 
              }}
            >
              {statusConfig.label}
            </span>
          </div>
          <div className={styles.headerActions}>
            {campaign.status === "PENDING" && (
              <button className={styles.startButton} onClick={handleStart}>
                ▶️ Pokreni kampanju
              </button>
            )}
            {campaign.status === "IN_PROGRESS" && (
              <>
                <button 
                  className={styles.refreshButton} 
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? "Osvežavanje..." : "🔄 Osveži status"}
                </button>
                <button 
                  className={styles.cancelButton}
                  onClick={() => setCancelModalOpen(true)}
                >
                  ✕ Otkaži
                </button>
              </>
            )}
            {campaign.status === "COMPLETED" && (
              <button 
                className={styles.refreshButton} 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? "Osvežavanje..." : "🔄 Osveži"}
              </button>
            )}
          </div>
        </div>

        {/* Campaign Info */}
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <h3>Detalji kampanje</h3>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Ciljna verzija:</span>
              <span className={styles.infoValue}>{campaign.targetVersion}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>OTA URL:</span>
              <a href={campaign.otaUrl} target="_blank" rel="noopener noreferrer" className={styles.otaLink}>
                {campaign.otaUrl}
              </a>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Kreirao:</span>
              <span className={styles.infoValue}>
                {campaign.createdBy?.firstName} {campaign.createdBy?.lastName}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Kreirano:</span>
              <span className={styles.infoValue}>{formatDate(campaign.createdAt)}</span>
            </div>
            {campaign.startedAt && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Pokrenuto:</span>
                <span className={styles.infoValue}>{formatDate(campaign.startedAt)}</span>
              </div>
            )}
            {campaign.completedAt && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Završeno:</span>
                <span className={styles.infoValue}>{formatDate(campaign.completedAt)}</span>
              </div>
            )}
            {campaign.notes && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Napomene:</span>
                <span className={styles.infoValue}>{campaign.notes}</span>
              </div>
            )}
          </div>

          <div className={styles.infoCard}>
            <h3>Napredak</h3>
            <div className={styles.progressStats}>
              <div className={styles.progressMain}>
                <span className={styles.progressNumber}>{campaign.updatedDevices}</span>
                <span className={styles.progressTotal}>/ {campaign.totalDevices}</span>
              </div>
              <span className={styles.progressPercent}>{progress.toFixed(1)}%</span>
            </div>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {campaign.statusBreakdown && (
              <div className={styles.breakdownGrid}>
                {Object.entries(campaign.statusBreakdown).map(([status, count]) => {
                  const config = DEVICE_STATUS_CONFIG[status];
                  return (
                    <div 
                      key={status} 
                      className={styles.breakdownItem}
                      style={{ borderLeftColor: config?.color }}
                    >
                      <span className={styles.breakdownCount}>{count}</span>
                      <span className={styles.breakdownLabel}>{config?.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Devices Table */}
        <div className={styles.devicesSection}>
          <div className={styles.devicesSectionHeader}>
            <h3>Uređaji ({devices.length})</h3>
            <select
              className={styles.deviceStatusFilter}
              value={deviceStatusFilter}
              onChange={(e) => setDeviceStatusFilter(e.target.value)}
            >
              <option value="">Svi statusi</option>
              {Object.entries(DEVICE_STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          {devicesLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
            </div>
          ) : devices.length === 0 ? (
            <div className={styles.emptyDevices}>
              Nema uređaja sa izabranim statusom
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Uređaj</th>
                    <th>MAC adresa</th>
                    <th>Prethodna verzija</th>
                    <th>Trenutna verzija</th>
                    <th>Status</th>
                    <th>Komanda poslata</th>
                    <th>Greška</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => {
                    const deviceStatusConfig = DEVICE_STATUS_CONFIG[d.status] || DEVICE_STATUS_CONFIG.PENDING;
                    const isUpdated = d.fiscalDevice?.version === campaign.targetVersion;
                    
                    return (
                      <tr 
                        key={d.id}
                        className={classNames(
                          d.status === "FAILED" && styles.failedRow,
                          d.status === "UPDATED" && styles.updatedRow
                        )}
                      >
                        <td>
                          <a 
                            href={`/fiscal-devices/${d.fiscalDeviceId}/info`}
                            className={styles.deviceLink}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(`/fiscal-devices/${d.fiscalDeviceId}/info`);
                            }}
                          >
                            {d.fiscalDevice?.name || `Device ${d.fiscalDeviceId}`}
                          </a>
                        </td>
                        <td className={styles.monoText}>{d.fiscalDevice?.macAddress || "-"}</td>
                        <td className={styles.monoText}>{d.previousVersion || "-"}</td>
                        <td className={classNames(styles.monoText, isUpdated && styles.versionMatch)}>
                          {d.fiscalDevice?.version || "-"}
                          {isUpdated && " ✓"}
                        </td>
                        <td>
                          <span 
                            className={styles.deviceStatusBadge}
                            style={{ 
                              backgroundColor: deviceStatusConfig.bgColor, 
                              color: deviceStatusConfig.color 
                            }}
                          >
                            {deviceStatusConfig.label}
                          </span>
                        </td>
                        <td>{formatDate(d.commandSentAt)}</td>
                        <td className={styles.errorCell}>
                          {d.errorMessage || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Cancel Confirmation Modal */}
        <BaseModal
          modalOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          title="Otkaži kampanju"
        >
          <div className={styles.cancelConfirmation}>
            <p>Da li ste sigurni da želite da otkažete ovu kampanju?</p>
            <p className={styles.cancelWarning}>
              ⚠️ Uređaji koji su već primili komandu će nastaviti sa ažuriranjem.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setCancelModalOpen(false)}
              >
                Ne, vrati se
              </button>
              <button
                className={styles.modalConfirmButton}
                onClick={handleCancel}
              >
                Da, otkaži kampanju
              </button>
            </div>
          </div>
        </BaseModal>
      </div>
    </DashboardLayout>
  );
};

export default CampaignDetailScreen;

